import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleBody from '@/components/feature/ArticleBody';
import RelatedSection from '@/components/feature/RelatedSection';
import HotSidebar from '@/components/feature/HotSidebar';
import NotFoundState from '@/components/feature/NotFoundState';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { getItemById, getItemsByType, getRelated } from '@/utils/content';
import BookingModal from '@/pages/detail/service/components/BookingModal';

export default function ServiceDetail() {
  const { id = '' } = useParams();
  const item = getItemById('service', id);
  const [bookingOpen, setBookingOpen] = useState(false);

  useDocumentMeta({
    title: item ? `【${item.title}】 - 91网站` : '生活服务详情 - 91网站',
    description: item ? item.summary : '91网站生活服务详情页。',
    keywords: item ? item.tags?.join(',') || '91网站,生活服务' : '91网站,生活服务',
  });

  if (!item) {
    return (
      <SiteLayout>
        <NotFoundState
          message="该生活服务不存在或已下架，请返回生活频道浏览其他服务。"
          backTo="/category/life"
          backLabel="返回生活频道"
          secondaryTo="/"
          secondaryLabel="返回首页"
        />
      </SiteLayout>
    );
  }

  const related = getRelated(item, 4);
  const hot = [...getItemsByType('service')].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 6);

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: '生活频道', to: '/category/life' }, { label: item.title }]} />

        <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
              <img
                src={item.image}
                alt={item.title}
                title={item.title}
                className="h-56 w-full object-cover object-top md:h-80"
              />
              <div className="p-5 md:p-8">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                    {item.serviceType}
                  </span>
                  <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-700">
                    {item.subCategory}
                  </span>
                </div>
                <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
                  {item.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-xs text-secondary-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    更新日期：{item.publishTime.slice(0, 10)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-map-pin-line"></i>
                    服务区域：{item.area}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {item.views} 人浏览
                  </span>
                </div>

                <div className="mt-6">
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-file-list-3-line text-primary-500"></i>
                    服务介绍与操作指引
                  </h2>
                  <ArticleBody content={item.content} image={item.image} imageAlt={item.title} />
                </div>
              </div>
            </div>
          </div>

          <aside className="space-y-4 lg:col-span-4">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-secondary-500">参考价格</span>
                <span className="font-heading text-2xl font-bold text-accent-600">{item.price}</span>
              </div>
              <div className="mt-4 space-y-2.5 border-t border-background-200 pt-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-secondary-500">服务类型</span>
                  <span className="text-foreground-800">{item.serviceType}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-secondary-500">服务区域</span>
                  <span className="text-foreground-800">{item.area}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-secondary-500">服务方</span>
                  <span className="text-foreground-800">{item.source}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setBookingOpen(true)}
                className="mt-5 flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-calendar-check-line"></i>
                立即预约 / 咨询
              </button>
              <Link
                to="/contact"
                className="mt-2.5 flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-customer-service-2-line"></i>
                联系客服了解详情
              </Link>
              <p className="mt-3 text-[11px] leading-relaxed text-secondary-400">
                说明:页面预约入口为功能演示,提交后不会产生真实订单。
              </p>
            </div>

            <HotSidebar title="热门服务" items={hot} />
          </aside>
        </div>

        <RelatedSection title="相关服务推荐" items={related} moreTo="/category/life" />
      </div>

      <BookingModal open={bookingOpen} serviceName={item.title} onClose={() => setBookingOpen(false)} />
    </SiteLayout>
  );
}